import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  BarChart3,
  CarFront,
  History as HistoryIcon,
  Inbox,
  LogOut,
  MailCheck,
  XCircle,
  MessagesSquare,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { BookingEmails } from "@/components/admin/BookingEmails";
import { BookingTimeline } from "@/components/admin/BookingTimeline";
import { AvailabilityManager } from "@/components/admin/AvailabilityManager";
import { EmailInbox } from "@/components/admin/EmailInbox";
import { SalesReport } from "@/components/admin/SalesReport";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { BOOKING_STAGES, STAGE_META, stageClass, type BookingStage } from "@/lib/booking-stages";
import {
  declineBooking,
  isCurrentUserAdmin,
  listBookings,
  sendReconfirmation,
  updateBooking,
} from "@/lib/bookings.functions";


type Status = "pending" | "confirmed" | "cancelled" | "completed";
type ReconfStatus = "not_sent" | "sent" | "confirmed" | "no_response";

const STATUSES: Status[] = ["pending", "confirmed", "cancelled", "completed"];

const statusStyles: Record<Status, string> = {
  pending: "border-gold/40 bg-gold/10 text-gold",
  confirmed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-400",
  completed: "border-border bg-muted text-muted-foreground",
};

const reconfStyles: Record<ReconfStatus, string> = {
  not_sent: "border-border bg-muted text-muted-foreground",
  sent: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  confirmed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  no_response: "border-red-500/40 bg-red-500/10 text-red-400",
};

const reconfLabels: Record<ReconfStatus, string> = {
  not_sent: "Reconfirm · not sent",
  sent: "Reconfirm · awaiting reply",
  confirmed: "Reconfirmed by customer",
  no_response: "Reconfirm · no response",
};

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Booking Dashboard | Car Rental Bucharest" },
      { name: "description", content: "Internal Car Rental Bucharest dashboard used by staff to manage booking requests, reconfirmations, customer emails and newsletter subscribers." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Booking Dashboard | Car Rental Bucharest" },
      { property: "og:description", content: "Internal dashboard to manage booking requests." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fetchBookings = useServerFn(listBookings);
  const checkAdmin = useServerFn(isCurrentUserAdmin);
  const saveBooking = useServerFn(updateBooking);
  const declineReservation = useServerFn(declineBooking);
  const sendReminder = useServerFn(sendReconfirmation);
  const [filter, setFilter] = useState<Status | "all">("all");
  const [tab, setTab] = useState<"bookings" | "inbox" | "report" | "availability">("bookings");
  const [openEmails, setOpenEmails] = useState<string | null>(null);
  const [openTimeline, setOpenTimeline] = useState<string | null>(null);

  const adminQuery = useQuery({ queryKey: ["is-admin"], queryFn: () => checkAdmin({}) });
  const bookingsQuery = useQuery({
    queryKey: ["bookings"],
    queryFn: () => fetchBookings({}),
    enabled: adminQuery.data?.isAdmin === true,
  });

  const mutation = useMutation({
    mutationFn: (input: { id: string; status: Status }) => saveBooking({ data: input }),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast.success(`Booking marked as ${input.status}`, {
        description:
          input.status === "confirmed" ? "A confirmation email was sent to the customer." : undefined,
      });
    },
    onError: () => toast.error("Could not update this booking"),
  });

  const stageMutation = useMutation({
    mutationFn: (input: { id: string; stage: BookingStage }) => saveBooking({ data: input }),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history", input.id] });
      queryClient.invalidateQueries({ queryKey: ["sales-report"] });
      toast.success(`Stage set to ${STAGE_META[input.stage]?.en ?? input.stage}`, {
        description: STAGE_META[input.stage]?.ro ?? input.stage,
      });
    },
    onError: () => toast.error("Could not change the stage"),
  });

  const noResponseMutation = useMutation({
    mutationFn: (id: string) =>
      saveBooking({ data: { id, status: "cancelled", reconfirmationStatus: "no_response" } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast.success("Booking cancelled — no response");
    },
    onError: () => toast.error("Could not update this booking"),
  });

  const declineMutation = useMutation({
    mutationFn: (id: string) => declineReservation({ data: { id } }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
      toast.success("Reservation declined", {
        description: result.sent
          ? `Apology email sent with voucher ${result.code} (10% off).`
          : `Voucher ${result.code} issued, but the email could not be delivered.`,
      });
    },
    onError: () => toast.error("Could not decline this reservation"),
  });

  const reminderMutation = useMutation({
    mutationFn: (id: string) => sendReminder({ data: { id } }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast.success("Reconfirmation reminder sent", {
        description: result.sent
          ? "The customer received a secure confirmation link."
          : "Saved, but the email could not be delivered — check email settings.",
      });
    },
    onError: () => toast.error("Could not send the reminder"),
  });


  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  if (adminQuery.isLoading) {
    return <main className="container-page py-24 text-muted-foreground">Loading…</main>;
  }

  if (!adminQuery.data?.isAdmin) {
    return (
      <main className="container-page py-24">
        <h1 className="font-display text-2xl font-bold">Access restricted</h1>
        <p className="mt-3 max-w-md text-sm text-muted-foreground">
          Your account is signed in but does not have administrator rights. Ask an existing admin to
          grant your account access.
        </p>
        <Button variant="outline" className="mt-6" onClick={signOut}>
          <LogOut className="size-4" /> Sign out
        </Button>
      </main>
    );
  }

  const bookings = bookingsQuery.data ?? [];
  const visible = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <main className="container-page py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-gold">Administration</p>
          <h1 className="mt-2 font-display text-3xl font-bold">Booking requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {bookings.length} total · {bookings.filter((b) => b.status === "pending").length} awaiting
            review
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "bookings" ? (
          <Select value={filter} onValueChange={(v) => setFilter(v as Status | "all")}>
            <SelectTrigger aria-label="Filter bookings by status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          ) : null}
          <Button
            variant="outline"
            size="icon"
            aria-label="Refresh"
            onClick={() => bookingsQuery.refetch()}
          >
            <RefreshCw className="size-4" />
          </Button>
          <Button variant="outline" onClick={signOut}>
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
      </div>


      <div className="mt-6 flex gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setTab("bookings")}
          className={`flex items-center gap-2 border-b-2 px-3 pb-3 text-sm font-semibold transition ${
            tab === "bookings"
              ? "border-gold text-gold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <MessagesSquare className="size-4" /> Bookings
        </button>
        <button
          type="button"
          onClick={() => setTab("inbox")}
          className={`flex items-center gap-2 border-b-2 px-3 pb-3 text-sm font-semibold transition ${
            tab === "inbox"
              ? "border-gold text-gold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Inbox className="size-4" /> Inbox
        </button>
        <button
          type="button"
          onClick={() => setTab("report")}
          className={`flex items-center gap-2 border-b-2 px-3 pb-3 text-sm font-semibold transition ${
            tab === "report"
              ? "border-gold text-gold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <BarChart3 className="size-4" /> Sales report
        </button>
        <button
          type="button"
          onClick={() => setTab("availability")}
          className={`flex items-center gap-2 border-b-2 px-3 pb-3 text-sm font-semibold transition ${
            tab === "availability"
              ? "border-gold text-gold"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <CarFront className="size-4" /> Availability
        </button>
      </div>

      {tab === "inbox" ? <EmailInbox /> : null}
      {tab === "report" ? <SalesReport /> : null}
      {tab === "availability" ? <AvailabilityManager /> : null}

      {tab !== "bookings" ? null : bookingsQuery.isLoading ? (
        <p className="mt-10 text-sm text-muted-foreground">Loading bookings…</p>
      ) : visible.length === 0 ? (
        <p className="mt-10 text-sm text-muted-foreground">No bookings in this view yet.</p>
      ) : (
        <div className="mt-8 space-y-4">
          {visible.map((booking) => (
            <article
              key={booking.id}
              className="rounded-2xl border border-border bg-card p-5 shadow-card"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${stageClass((booking.stage ?? "new") as BookingStage)}`}
                    >
                      {STAGE_META[(booking.stage ?? "new") as BookingStage]?.ro ?? booking.stage}
                    </span>
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${statusStyles[booking.status as Status]}`}
                    >
                      {booking.status}
                    </span>

                    {booking.pickup_type === "otopeni" || booking.dropoff_type === "otopeni" ? (
                      <span className="rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-gold">
                        Otopeni airport
                      </span>
                    ) : null}
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Ref {booking.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <h2 className="mt-2 font-display text-lg font-bold">{booking.car_name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {booking.pickup_date} → {booking.return_date} · {booking.pickup_location} →{" "}
                    {booking.dropoff_location ?? booking.pickup_location}
                    {Number(booking.delivery_fee) > 0 ? ` · +€${Number(booking.delivery_fee)} delivery` : ""}
                    {Number(booking.airport_fee) > 0 ? ` · +€${Number(booking.airport_fee)} airport` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl font-bold text-gold">
                    €{Number(booking.total_estimate ?? 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    €{Number(booking.price_per_day ?? 0)} / day
                  </p>
                </div>
              </div>

              <dl className="mt-4 grid gap-3 border-t border-border pt-4 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Customer</dt>
                  <dd className="mt-0.5">{booking.customer_name}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Email</dt>
                  <dd className="mt-0.5 break-all">
                    <a href={`mailto:${booking.customer_email}`} className="hover:text-gold">
                      {booking.customer_email}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Phone</dt>
                  <dd className="mt-0.5">
                    <a href={`tel:${booking.customer_phone}`} className="hover:text-gold">
                      {booking.customer_phone}
                    </a>
                  </dd>
                </div>
              </dl>

              {booking.notes ? (
                <p className="mt-3 rounded-lg border border-border bg-background/50 p-3 text-sm text-muted-foreground">
                  {booking.notes}
                </p>
              ) : null}

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
                <span
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${reconfStyles[(booking.reconfirmation_status ?? "not_sent") as ReconfStatus]}`}
                >
                  {reconfLabels[(booking.reconfirmation_status ?? "not_sent") as ReconfStatus]}
                </span>
                {booking.reconfirmation_sent_at ? (
                  <span className="text-xs text-muted-foreground">
                    sent {new Date(booking.reconfirmation_sent_at).toLocaleString()}
                  </span>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={reminderMutation.isPending}
                  onClick={() => reminderMutation.mutate(booking.id)}
                >
                  <MailCheck className="size-4" />
                  {booking.reconfirmation_status === "sent" ? "Resend reconfirmation" : "Send reconfirmation reminder"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={declineMutation.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Decline this reservation? The customer receives an apology email with a 10% voucher for their next booking.",
                      )
                    )
                      declineMutation.mutate(booking.id);
                  }}
                  className="text-red-400 hover:text-red-300"
                >
                  <XCircle className="size-4" />
                  Decline &amp; send 10% voucher
                </Button>
                {booking.reconfirmation_status === "sent" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={noResponseMutation.isPending}
                    onClick={() => noResponseMutation.mutate(booking.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Mark cancelled — no response
                  </Button>
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Lifecycle stage
                </span>
                <Select
                  value={(booking.stage ?? "new") as BookingStage}
                  onValueChange={(v) =>
                    stageMutation.mutate({ id: booking.id, stage: v as BookingStage })
                  }
                >
                  <SelectTrigger aria-label="Change booking stage" className="w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BOOKING_STAGES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STAGE_META[s]?.ro} — {STAGE_META[s]?.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOpenEmails(openEmails === booking.id ? null : booking.id)}
                >
                  <Inbox className="size-4" />
                  {openEmails === booking.id ? "Hide emails" : "Email history"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setOpenTimeline(openTimeline === booking.id ? null : booking.id)
                  }
                >
                  <HistoryIcon className="size-4" />
                  {openTimeline === booking.id ? "Hide timeline" : "Timeline"}
                </Button>
              </div>

              {openTimeline === booking.id ? <BookingTimeline bookingId={booking.id} /> : null}
              {openEmails === booking.id ? <BookingEmails bookingId={booking.id} /> : null}

            </article>

          ))}
        </div>
      )}
    </main>
  );
}
